import axios from "axios";
import posthog from "posthog-js";
import { toast } from "sonner";

import { pitchGetter } from "@/app/(application)/(normal)/create/components/input/pitch";
import { AUDIO_DECODE_FAILURE_MESSAGE } from "@/constants/audio";
import { AUDIO_QUALITY_CHECK_STATUS } from "@/constants/audioQuality";
import { defaultPitchShift } from "@/constants/defaultValues";
import { conversionNamespace } from "@/constants/namespaces";
import { createSoundFullAction } from "@/data/sound/actions/createSoundFullAction";
import { useConversionHyperParameters } from "@/lib/zustand/useConversionHyperParameters";
import { useNamedStore } from "@/lib/zustand/useNamedStore";
import { useNamedSelectedArtist } from "@/lib/zustand/useSelectedArtist";
import { AudioQualityCheck } from "@/types/defs/audioQuality/audioQualityCheck";
import { ModelPitchRange } from "@/types/defs/modelPitchRange";
import { SelectedFile } from "@/types/defs/selectedFile";
import { AudioInputState, Cover, Sound } from "@/types/types";
import { convertAiffToWav } from "@/utils/audio/aiff/convertAiff";
import { isAiffFile } from "@/utils/audio/aiff/isAiffFile";
import { checkAudioQuality } from "@/utils/audio/quality/checkAudioQuality";
import { checkAudioQualityForModelCreation } from "@/utils/audio/quality/checkAudioQualityForModelCreation";
import { getFileExtension } from "@/utils/filenameExtension/getFileExtension";
import { getAudioLengthClient } from "@/utils/getAudioLength/getAudioLengthClient";
import { logError } from "@/utils/logError";
import { isSelectedCover } from "@/utils/selectedFileCover/is/isSelectedCover";
import { isSelectedFile } from "@/utils/selectedFileCover/is/isSelectedFile";
import { isAbortError } from "@/utils/typeGuards/isAbortError";
import { isOfflineNetworkError } from "@/utils/typeGuards/isOfflineNetworkError";
import { parseSupportedAudioFileType } from "@/utils/typeGuards/parseSupportedAudioFileType";

const CONVERSION_NAMESPACES = ["conversion"];
const MODEL_NAMESPACES = ["model"];
const PITCH_GETTER_FEATURES = CONVERSION_NAMESPACES;

// Ever-increasing counter shared across all namespaced stores, used to stamp each input with the order it was filled in.
let lastInputOrder = 0;
const nextInputOrder = () => ++lastInputOrder;

const audioInput = (
  set: (partial: Partial<AudioInputState>) => void,
  get: () => AudioInputState,
  namespace: string
) => {
  const shouldSetPitchGetter = PITCH_GETTER_FEATURES.some((keyword) =>
    namespace.includes(keyword)
  );
  const isConversionNamespace = CONVERSION_NAMESPACES.some((keyword) =>
    namespace.includes(keyword)
  );
  const isModelNamespace = MODEL_NAMESPACES.some((keyword) =>
    namespace.includes(keyword)
  );

  const getSelectedFileCover = () => {
    const state = get();
    return state.selectedFileCover;
  };

  const getSelectedModelPitchRange = (): ModelPitchRange => {
    const selectedArtistStore = useNamedSelectedArtist
      .getState()
      .getNamedStore(conversionNamespace);
    return selectedArtistStore.getState().selectedModelPitchRange;
  };

  // Conversions only.
  const isAudioQualityCheckEnabled = (): boolean => isConversionNamespace;

  // Model training uploads run the effects-only check — there is no artist/pitch involved, so no pitch range required.
  const isModelQualityCheckEnabled = (): boolean => isModelNamespace;

  const clearPreviousInput = () => {
    const selectedFileCover = getSelectedFileCover();
    if (selectedFileCover !== null && isSelectedFile(selectedFileCover)) {
      URL.revokeObjectURL(selectedFileCover.selectedUrl);
      selectedFileCover.abortController.abort();
    }

    if (isConversionNamespace) {
      const { setPitch } = useConversionHyperParameters.getState();
      setPitch(defaultPitchShift);
    }
  };

  const hasInputChanged = (expectedUrl: string) => {
    const selectedFileCover = getSelectedFileCover();
    return (
      selectedFileCover === null ||
      isSelectedCover(selectedFileCover) ||
      selectedFileCover.selectedUrl !== expectedUrl
    );
  };

  const uploadFileToStorage = async (
    file: File,
    presignedUrl: string,
    abortController: AbortController,
    expectedUrl: string
  ) => {
    await axios.put(presignedUrl, file, {
      onUploadProgress: (progressEvent) => {
        // For some files progressEvent.total is undefined and thus we cannot calculate progress
        if (progressEvent.total === undefined || hasInputChanged(expectedUrl)) {
          return;
        }

        const selectedFileCover = getSelectedFileCover();
        if (selectedFileCover !== null && isSelectedFile(selectedFileCover)) {
          // Never report null here: the last progress event fires when the final byte is sent, before R2 confirms the PUT. progress === null must keep meaning "the file exists in storage".
          set({
            selectedFileCover: {
              ...selectedFileCover,
              progress: progressEvent.loaded / progressEvent.total,
            },
          });
        }
      },
      signal: abortController.signal,
      headers: {
        "Content-Type": file.type,
      },
    });
  };

  const runAudioQualityCheck = ({
    processedFile,
    isFirstFile,
    signal,
  }: {
    processedFile: File;
    isFirstFile: boolean;
    signal: AbortSignal;
  }): Promise<AudioQualityCheck | null> => {
    if (isModelQualityCheckEnabled()) {
      return checkAudioQualityForModelCreation({
        audioFile: processedFile,
        signal,
      });
    }

    const pitchRange = getSelectedModelPitchRange();
    if (!isAudioQualityCheckEnabled() || pitchRange === null) {
      return Promise.resolve(null);
    }
    return checkAudioQuality({
      audioFile: processedFile,
      // If sweetspot present we send artist sweetspot instead of a full range
      sweetSpot: pitchRange,
      shouldCheckPitchAnalysis: isFirstFile,
      signal,
    });
  };

  // expectedUrl identifies the input this failure belongs to. Only the input that actually failed is toasted and cleared — by the time a slow request rejects the user may have swapped in a healthy file, and clearing that one would discard work the failure never touched.
  const handleUploadError = (expectedUrl: string, error: unknown) => {
    if (isAbortError(error)) {
      return;
    }

    if (hasInputChanged(expectedUrl)) {
      // The user already discarded this input, so a toast would describe a file nothing on screen relates to. The capture stays so error stats still count the failure. Same offline rule as logError below: otherwise swapping the file would decide whether connectivity noise gets reported.
      console.error(error);
      if (!isOfflineNetworkError(error)) {
        posthog.captureException(error);
      }
      return;
    }

    logError(error);
    get().clearInput();
  };

  // Shallow-merges a patch into the current selectedFileCover, guarding against races where the user cleared or swapped the input while an async branch was in flight.
  const mergeIntoSelectedFile = (
    expectedUrl: string,
    patch: Partial<SelectedFile>
  ) => {
    if (hasInputChanged(expectedUrl)) {
      return;
    }
    const currentSelectedFileCover = getSelectedFileCover();
    if (
      currentSelectedFileCover === null ||
      !isSelectedFile(currentSelectedFileCover)
    ) {
      return;
    }
    set({
      selectedFileCover: { ...currentSelectedFileCover, ...patch },
    });
  };

  return {
    pitchGetter: null,
    selectedFileCover: null,
    inputOrder: null,

    clearInput: () => {
      clearPreviousInput();
      set({
        selectedFileCover: null,
        pitchGetter: null,
        inputOrder: null,
      });
    },

    setInputCover: (cover: Cover) => {
      clearPreviousInput();
      set({
        selectedFileCover: cover,
        inputOrder: nextInputOrder(),
      });
    },

    setInputSound: (sound: Sound) => {
      clearPreviousInput();
      const newSelectedFileCover = {
        selectedUrl: sound.presignedUrlPlay,
        selectedSound: sound,
        progress: null,
        abortController: new AbortController(),
        originalFileFormat: null,
        audioQualityCheck: null,
        audioQualityCheckStatus: AUDIO_QUALITY_CHECK_STATUS.skipped,
      };
      set({
        selectedFileCover: newSelectedFileCover,
        pitchGetter:
          shouldSetPitchGetter ? pitchGetter(newSelectedFileCover)() : null,
        inputOrder: nextInputOrder(),
      });
    },

    setInputFile: async ({
      file,
      isFirstFile,
    }: {
      file: File;
      isFirstFile: boolean;
    }) => {
      clearPreviousInput();

      // Get original file format from the original file name
      let fileExtension = getFileExtension(file.name);

      // We are sending to ai backend .aiff file type even if it is .aif
      if (fileExtension === "aif") {
        fileExtension = "aiff";
      }

      if (fileExtension === null) {
        throw new Error("No file extension found");
      }

      const originalFileFormat = parseSupportedAudioFileType(fileExtension);

      // Converting to wav if it is an aiff file
      const isAiff = isAiffFile(file);
      const processedFile = isAiff ? await convertAiffToWav(file) : file;

      const selectedUrl = URL.createObjectURL(processedFile);
      const abortController = new AbortController();

      // Set input to file so it is displayed in the UI even before upload is started
      set({
        selectedFileCover: {
          selectedUrl,
          selectedSound: null,
          progress: 0,
          abortController,
          originalFileFormat,
          audioQualityCheck: null,
          audioQualityCheckStatus:
            (
              isModelQualityCheckEnabled() ||
              (isAudioQualityCheckEnabled() &&
                getSelectedModelPitchRange() !== null)
            ) ?
              AUDIO_QUALITY_CHECK_STATUS.pending
            : AUDIO_QUALITY_CHECK_STATUS.skipped,
        },
        inputOrder: nextInputOrder(),
      });

      // getAudioLengthClient already captured the decode failure with per-file context, so this path only tells the user and clears the input — routing it through handleUploadError would capture it a second time and toast the raw decoder message. Callers usually pre-measure via handleFileUpload, but that measures the original file while this measures the converted one (AIFF), and the tutorial calls setInputFile directly.
      let audioLengthS: number;
      try {
        audioLengthS = await getAudioLengthClient(processedFile);
      } catch (error) {
        console.error(error);
        if (hasInputChanged(selectedUrl)) {
          URL.revokeObjectURL(selectedUrl);
          return;
        }
        // Shared id: a multi-file drop whose converted files all fail to decode describes one problem, so it collapses into one toast
        toast.error(AUDIO_DECODE_FAILURE_MESSAGE, { id: "audio-decode" });
        get().clearInput();
        return;
      }

      // Callers fire setInputFile without awaiting, so a rejection here (e.g. the server action failing while offline) would otherwise become an unhandled rejection: no toast, and the input stuck at progress 0.
      let soundResponse: Awaited<ReturnType<typeof createSoundFullAction>>;
      try {
        soundResponse = await createSoundFullAction({
          filename: processedFile.name,
          lengthS: audioLengthS,
        });
      } catch (error) {
        handleUploadError(selectedUrl, error);
        return;
      }

      if (hasInputChanged(selectedUrl)) {
        URL.revokeObjectURL(selectedUrl);
        return;
      }

      // Attach the sound record now so downstream UI can use it before the upload finishes.
      mergeIntoSelectedFile(selectedUrl, {
        selectedSound: soundResponse.sound,
      });

      const uploadAndMarkFinished = async () => {
        await uploadFileToStorage(
          processedFile,
          soundResponse.presignedUrlPutFile,
          abortController,
          selectedUrl
        );
        // R2 has confirmed the PUT — only now is the signed GET URL safe to use.
        mergeIntoSelectedFile(selectedUrl, { progress: null });
      };

      // Quality check and storage upload run in parallel and fail independently. The shared abortController kills both on clear/swap.
      const [qualityResult, uploadResult] = await Promise.allSettled([
        runAudioQualityCheck({
          processedFile,
          isFirstFile,
          signal: abortController.signal,
        }),
        uploadAndMarkFinished(),
      ]);

      if (qualityResult.status === "rejected") {
        if (!isAbortError(qualityResult.reason)) {
          logError(qualityResult.reason);
          mergeIntoSelectedFile(selectedUrl, {
            audioQualityCheckStatus: AUDIO_QUALITY_CHECK_STATUS.complete,
          });
        }
      } else if (qualityResult.value !== null) {
        mergeIntoSelectedFile(selectedUrl, {
          audioQualityCheck: qualityResult.value,
          audioQualityCheckStatus: AUDIO_QUALITY_CHECK_STATUS.complete,
        });
      } else {
        // value === null: the check was disabled or there was no artist.
        mergeIntoSelectedFile(selectedUrl, {
          audioQualityCheckStatus: AUDIO_QUALITY_CHECK_STATUS.skipped,
        });
      }

      if (uploadResult.status === "rejected") {
        return handleUploadError(selectedUrl, uploadResult.reason);
      }

      if (hasInputChanged(selectedUrl)) {
        URL.revokeObjectURL(selectedUrl);
        return;
      }

      const newSelectedFileCover = {
        ...getSelectedFileCover()!,
        progress: null,
      };
      set({
        selectedFileCover: newSelectedFileCover,
        pitchGetter:
          shouldSetPitchGetter ? pitchGetter(newSelectedFileCover)() : null,
      });
    },
  };
};

export const useAudioInput = useNamedStore<AudioInputState>(
  (namespace) => (set, get) => audioInput(set, get, namespace)
);
